import { useState, type CSSProperties, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import type { IconName } from '@/components/ui/Icon';
import { useI18n } from '@/hooks/useI18n';
import { DRAWER_ROLE_SWITCH } from '@/lib/navigation';
import type { WorkspaceKpi, WorkspaceModule, WorkspaceProfile } from '../types';

const ICONS: Record<string, IconName> = {
  briefcase: 'briefcase',
  chat: 'chat',
  shield: 'shield',
  user: 'user',
  chart: 'chart',
  news: 'news',
  calendar: 'calendar',
  document: 'document',
  graduation: 'graduation',
  building: 'building',
  trophy: 'trophy',
};

const ACCENTS = ['#41d2ff', '#49ddb7', '#e2ad3a', '#9e80ff', '#66b7e8', '#ff7a64'];

interface WorkspaceHeroProps {
  profile: WorkspaceProfile;
  kpis: WorkspaceKpi[];
  createRequestTo: string;
  assistantTo: string;
  accent?: string;
}

export const WorkspaceHero = ({
  profile,
  kpis,
  createRequestTo,
  assistantTo,
  accent = '#ff7b65',
}: WorkspaceHeroProps) => {
  const { t, locale } = useI18n();
  const roleTitle =
    DRAWER_ROLE_SWITCH.find((item) => item.code === profile.role)?.label ??
    profile.position ??
    profile.role;

  return (
    <section className="role-cabinet">
      <div className="role-hero" style={{ '--role-accent': accent } as CSSProperties}>
        <div>
          <small>{profile.academia_id || t('workspace.status.online')}</small>
          <h2>{profile.full_name}</h2>
          <p>
            {profile.position ?? roleTitle} · {t('workspace.status.online')}
          </p>
        </div>
        <div className="role-hero-actions">
          <Link to={assistantTo} className="role-btn">
            {t('workspace.superAi')}
          </Link>
          <Link to={createRequestTo} className="role-btn primary">
            + {t('workspace.createRequest')}
          </Link>
        </div>
      </div>

      {kpis.length > 0 ? (
        <div className="role-statgrid">
          {kpis.slice(0, 4).map((kpi, index) => (
            <div
              key={kpi.key}
              className="role-stat"
              style={{ '--a': ACCENTS[index % ACCENTS.length] } as CSSProperties}
            >
              <strong>{kpi.value}</strong>
              <span>{kpi.label[locale]}</span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

interface ModuleGridProps {
  modules: WorkspaceModule[];
  basePath: string;
  /** Role-specific presentation so cabinets don't share one generic ModuleGrid look. */
  theme?: {
    accents?: string[];
    title?: string;
    subtitle?: string;
    cta?: string;
    className?: string;
  };
}

export const ModuleGrid = ({ modules, basePath, theme }: ModuleGridProps) => {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const [openingKey, setOpeningKey] = useState<string | null>(null);
  const accents = theme?.accents?.length ? theme.accents : ACCENTS;
  const cta = theme?.cta ?? 'Открыть →';

  const openModule = (event: MouseEvent, key: string, to: string) => {
    event.preventDefault();
    if (openingKey) return;
    setOpeningKey(key);
    window.setTimeout(() => navigate(to), 180);
  };

  return (
    <div className={`workspace-grid owner-hub-grid${theme?.className ? ` ${theme.className}` : ''}`}>
      {theme?.title ? (
        <header className="role-module-grid-head">
          <h3>{theme.title}</h3>
          {theme.subtitle ? <p>{theme.subtitle}</p> : null}
        </header>
      ) : null}
      {modules.map((module, index) => {
        const to = `${basePath}/${module.key}`;
        return (
          <Link
            key={module.key}
            to={to}
            className={`owner-hub-card${openingKey === module.key ? ' is-opening' : ''}`}
            style={{ '--action': accents[index % accents.length] } as CSSProperties}
            data-order={module.order}
            onClick={(event) => openModule(event, module.key, to)}
          >
            <span className="owner-hub-index">{String(module.order).padStart(2, '0')}</span>
            <span className="owner-hub-icon">
              <Icon name={ICONS[module.icon] ?? 'document'} size={22} />
            </span>
            <span className="owner-hub-copy">
              <strong>{module.label[locale]}</strong>
              <span>{module.description[locale]}</span>
              <em className="owner-hub-cta">{cta}</em>
            </span>
            <span className="owner-hub-arrow" aria-hidden="true">
              ›
            </span>
          </Link>
        );
      })}
    </div>
  );
};
